import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { startReportGenerationMutation } from '../../graphql/mutations';
import {
  Alert,
  Button,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { InvoiceMonthSelector } from '../../components/InvoiceMonthSelector';
import moment from 'moment';
import { ReportType } from '../../graphql/generatedTypes';
const initialYear = moment()
  .subtract(1, 'months')
  .endOf('month')
  .year()
  .toString();
const initialMonth = (
  moment().subtract(1, 'months').endOf('month').month() + 1
).toString();

/** Kept in step with MAX_INVOICE_NUMBERS in the colugo report. */
const MAX_INVOICE_NUMBERS = 1000;

function enumKeys<O extends object, K extends keyof O = keyof O>(obj: O): K[] {
  return Object.keys(obj).filter((k) => Number.isNaN(+k)) as K[];
}

function parseInvoiceNumbers(invoiceNumbersText: string): string[] {
  const seen = new Set<string>();
  const invoiceNumbers: string[] = [];
  for (const candidate of invoiceNumbersText.split(/[\s,;]+/)) {
    const invoiceNumber = candidate.trim();
    if (!invoiceNumber) continue;
    const key = invoiceNumber.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    invoiceNumbers.push(invoiceNumber);
  }
  return invoiceNumbers;
}

export function ReportGeneration() {
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);

  const [reportType, setReportType] = useState('');
  const [invoiceNumbersText, setInvoiceNumbersText] = useState('');
  const [startReportGeneration, { loading, error }] = useMutation(
    startReportGenerationMutation,
  );

  const requiresInvoiceNumbers =
    reportType === ReportType.BillomatInvoicePdfArchive;
  const invoiceNumbers = useMemo(
    () => parseInvoiceNumbers(invoiceNumbersText),
    [invoiceNumbersText],
  );

  const onStartReportGeneration = async () => {
    if (!reportType) {
      return alert('Please select a report type');
    }
    if (requiresInvoiceNumbers) {
      if (invoiceNumbers.length === 0) {
        return alert(
          'Please enter at least one invoice number for the ' +
            reportType +
            ' report',
        );
      }
      if (invoiceNumbers.length > MAX_INVOICE_NUMBERS) {
        return alert(
          'Please enter at most ' +
            MAX_INVOICE_NUMBERS +
            ' invoice numbers, you entered ' +
            invoiceNumbers.length +
            '. Start several reports instead.',
        );
      }
      if (
        !confirm(
          'are you sure that you want to start a ' +
            reportType +
            ' report generation for ' +
            invoiceNumbers.length +
            ' invoice numbers?',
        )
      ) {
        return;
      }
    } else if (
      !confirm(
        'are you sure that you want to start a ' +
          reportType +
          ' report generation for the month: ' +
          selectedYear +
          '-' +
          selectedMonth.toString().padStart(2, '0') +
          '?',
      )
    ) {
      return;
    }
    await startReportGeneration({
      variables: {
        year: Number(selectedYear),
        month: Number(selectedMonth),
        report_type: reportType,
        invoice_numbers: requiresInvoiceNumbers ? invoiceNumbers : null,
      },
    });
    alert(
      'Report generation started. Check the #fin-reports slack channel for the report result link',
    );
  };

  return (
    <>
      <h5>Report Generation</h5>
      <FormGroup>
        <Label for="financeReportType">Report Type</Label>
        <Input
          type="select"
          name="financeReportType"
          id="financeReportType"
          onChange={(e) => setReportType(e.target.value)}
        >
          <option value={''}>Please select</option>
          {enumKeys(ReportType).map((key) => (
            <option key={key} value={ReportType[key]}>
              {ReportType[key]}
            </option>
          ))}
        </Input>
      </FormGroup>

      {error &&
        error.graphQLErrors &&
        error.graphQLErrors.map((errorMessage, index) => (
          <Alert key={index} color="danger">
            Error: {errorMessage.message}
          </Alert>
        ))}

      {requiresInvoiceNumbers ? (
        <FormGroup>
          <Label for="invoiceNumbers">Invoice numbers</Label>
          <Input
            type="textarea"
            name="invoiceNumbers"
            id="invoiceNumbers"
            rows={8}
            placeholder={'RE12345\nRE12346\nRE12347'}
            value={invoiceNumbersText}
            onChange={(e) => setInvoiceNumbersText(e.target.value)}
          />
          <FormText color="muted">
            One invoice number per line. A comma, a semicolon and a space also
            separate two numbers. {invoiceNumbers.length} invoice number
            {invoiceNumbers.length === 1 ? '' : 's'} recognized, at most{' '}
            {MAX_INVOICE_NUMBERS} per report. The report delivers one ZIP
            archive that holds one PDF per invoice plus a manifest.csv with the
            result of every number.
          </FormText>
        </FormGroup>
      ) : (
        <Row>
          <InvoiceMonthSelector
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
          />
        </Row>
      )}

      <FormText color="muted">
        Please do not start multiple report generations at the same time which
        do consume Billomat API credits. Check notion to get more information
        about each report type. When the report finished, you will be notified
        in the private #fin-reports slack channel.
      </FormText>
      <br />

      <Button disabled={loading} onClick={onStartReportGeneration}>
        {loading ? 'loading...' : 'Start report generation'}
      </Button>
    </>
  );
}
